import React, { useState, useMemo, useCallback } from "react";
import { X, Download, Loader2, FileImage, FileText, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  renderMapExport,
  chooseFitZoom,
  computePointsBounds,
  downloadCanvasAsJpg,
  downloadCanvasAsPdf,
  defaultExportFilename,
} from "@/utils/mapExport";

const OVERLAY_STATUS_COLORS = {
  delivered: "#10b981", not_found: "#94a3b8", promised_pay: "#0ea5e9", pending: "#ef4444",
  lunas: "#10b981", tidak_ketemu: "#94a3b8", janji_bayar: "#0ea5e9", ditagih: "#f59e0b",
};
const statusColor = (s) => {
  const v = String(s || "").toLowerCase();
  if (v.includes("non")) return "#ef4444";
  if (v.includes("aktif")) return "#10b981";
  return "#64748b";
};

// Max tile-side (in pixels) for the exported image. Roughly ~2400 keeps quality
// crisp while capping tile count so exports finish in seconds even on 4G.
const MAX_SIDE_PX = 2400;

/**
 * Props:
 *   mapRef            — ref to the Leaflet map (for viewport bounds/zoom)
 *   base              — current base layer key ('satellite'|'street'|'hybrid')
 *   allPoints         — [{ cid, lat, lng, color }] for "Semua Pin Terfilter" export
 *   viewportPoints    — [{ cid, lat, lng, color }] for "Tampilan Saat Ini" export
 *   showIdLabels      — whether ID labels are currently shown on the map
 *   onClose
 */
export default function ExportMapModal({
  mapRef, base, allPoints = [], viewportPoints = [], showIdLabels = false, onClose,
}) {
  const [scope, setScope] = useState("viewport"); // 'viewport' | 'all'
  const [format, setFormat] = useState("jpg"); // 'jpg' | 'pdf'
  const [includeLabels, setIncludeLabels] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const canExportAll = allPoints.length > 0;

  const activePoints = scope === "all" ? allPoints : viewportPoints;

  const bounds = useMemo(() => {
    if (scope === "all") {
      return computePointsBounds(allPoints);
    }
    const m = mapRef.current;
    if (!m) return null;
    const b = m.getBounds();
    return {
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    };
  }, [scope, allPoints, mapRef]);

  const targetZoom = useMemo(() => {
    if (!bounds) return 14;
    if (scope === "viewport" && mapRef.current) {
      // Match what's on-screen (rounded up to nearest integer for detail).
      const z = Math.round(mapRef.current.getZoom());
      return Math.max(1, Math.min(z, 20));
    }
    return chooseFitZoom(bounds, MAX_SIDE_PX, 20);
  }, [bounds, scope, mapRef]);

  const doExport = useCallback(async () => {
    if (!bounds) {
      toast.error("Tidak ada area untuk di-export.");
      return;
    }
    if (scope === "all" && !canExportAll) {
      toast.error("Tidak ada pin terfilter untuk di-export.");
      return;
    }
    setBusy(true);
    setProgress({ done: 0, total: 0 });
    try {
      const canvas = await renderMapExport({
        bounds,
        zoom: targetZoom,
        base,
        points: activePoints,
        showIdLabels: includeLabels,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const prefix = scope === "all" ? "peta-global-semua" : "peta-global-tampilan";
      const filename = defaultExportFilename(prefix, format);
      if (format === "pdf") {
        await downloadCanvasAsPdf(canvas, filename);
      } else {
        downloadCanvasAsJpg(canvas, filename);
      }
      toast.success(`Berhasil export: ${filename}`);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Gagal export peta: " + (err?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  }, [bounds, scope, canExportAll, activePoints, targetZoom, base, includeLabels, format, onClose]);

  const estTiles = useMemo(() => {
    if (!bounds) return 0;
    const nwX = ((bounds.west + 180) / 360) * 256 * Math.pow(2, targetZoom);
    const nwY = (1 - Math.log(Math.tan((bounds.north * Math.PI) / 180) + 1 / Math.cos((bounds.north * Math.PI) / 180)) / Math.PI) * 0.5 * 256 * Math.pow(2, targetZoom);
    const seX = ((bounds.east + 180) / 360) * 256 * Math.pow(2, targetZoom);
    const seY = (1 - Math.log(Math.tan((bounds.south * Math.PI) / 180) + 1 / Math.cos((bounds.south * Math.PI) / 180)) / Math.PI) * 0.5 * 256 * Math.pow(2, targetZoom);
    const w = Math.ceil(seX / 256) - Math.floor(nwX / 256) + 1;
    const h = Math.ceil(seY / 256) - Math.floor(nwY / 256) + 1;
    return Math.max(1, w * h);
  }, [bounds, targetZoom]);

  return (
    <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-3" data-testid="export-map-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Download className="w-4 h-4 text-emerald-600" /> Export Peta
          </h3>
          <button onClick={onClose} disabled={busy} data-testid="export-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Scope */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">Area yang di-export</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setScope("viewport")}
                disabled={busy}
                data-testid="export-scope-viewport"
                className={`text-left border rounded-lg p-2.5 text-xs transition ${
                  scope === "viewport"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                <div className="font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" /> Tampilan Saat Ini</div>
                <div className="text-[10px] mt-0.5 opacity-80">{viewportPoints.length} pin di layar</div>
              </button>
              <button
                type="button"
                onClick={() => setScope("all")}
                disabled={busy || !canExportAll}
                data-testid="export-scope-all"
                className={`text-left border rounded-lg p-2.5 text-xs transition ${
                  scope === "all"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                } ${!canExportAll ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" /> Semua Pin Terfilter</div>
                <div className="text-[10px] mt-0.5 opacity-80">{allPoints.length} pin total</div>
              </button>
            </div>
          </div>

          {/* Format */}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">Format file</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormat("jpg")}
                disabled={busy}
                data-testid="export-format-jpg"
                className={`border rounded-lg p-2.5 text-xs flex items-center justify-center gap-1.5 transition ${
                  format === "jpg" ? "border-cyan-500 bg-cyan-50 text-cyan-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                <FileImage className="w-4 h-4" /> JPG
              </button>
              <button
                type="button"
                onClick={() => setFormat("pdf")}
                disabled={busy}
                data-testid="export-format-pdf"
                className={`border rounded-lg p-2.5 text-xs flex items-center justify-center gap-1.5 transition ${
                  format === "pdf" ? "border-cyan-500 bg-cyan-50 text-cyan-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                <FileText className="w-4 h-4" /> PDF
              </button>
            </div>
          </div>

          {/* Labels toggle */}
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={includeLabels}
              onChange={(e) => setIncludeLabels(e.target.checked)}
              disabled={busy}
              className="w-4 h-4"
              data-testid="export-labels-toggle"
            />
            Sertakan label ID pelanggan di atas titik
          </label>

          {/* Info */}
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 leading-relaxed" data-testid="export-info">
            <div>Layer: <b className="font-mono">{base}</b> · Zoom target: <b className="font-mono">{targetZoom}</b> · ~{estTiles} tile</div>
            <div className="mt-0.5">Pin di-export: <b>{activePoints.length}</b></div>
            {estTiles > 300 && (
              <div className="mt-1 text-amber-700">⚠️ Area cukup luas ({estTiles} tile). Export bisa lambat di jaringan pelan.</div>
            )}
          </div>

          {/* Progress */}
          {busy && (
            <div data-testid="export-progress">
              <div className="text-[11px] text-slate-600 mb-1">
                Mengambil tile & menggambar... {progress.done}/{progress.total || estTiles}
              </div>
              <div className="w-full h-1.5 bg-slate-200 rounded overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{
                    width: `${
                      progress.total
                        ? Math.round((progress.done / progress.total) * 100)
                        : 5
                    }%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy} data-testid="export-cancel-btn">
            Batal
          </Button>
          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            onClick={doExport}
            disabled={busy || !bounds || (scope === "all" && !canExportAll)}
            data-testid="export-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export {format.toUpperCase()}
          </Button>
        </div>
      </div>
    </div>
  );
}
